'use client';

import { useCallback, useEffect, useState } from 'react';
import type { Orientation } from '@/types/constants';
import { ORIENTATIONS } from '@/utils/constants';
import { useDebounce } from '@/hooks/use-debounce';

type UseDeviceOrientationOptions = {
    breakpoint?: number;
    debounceMs?: number;
    initialIsMobile?: boolean;
};

export const useDeviceOrientation = ({
    breakpoint = 768,
    debounceMs = 100,
    initialIsMobile = true
}: UseDeviceOrientationOptions = {}): Orientation => {
    const [orientation, setOrientation] = useState<Orientation>(
        initialIsMobile ? ORIENTATIONS.PORTRAIT : ORIENTATIONS.LANDSCAPE
    );

    const handleWindowSizeChange = useCallback(() => {
        const { innerWidth: width } = window || {};
        const layout = width >= breakpoint ? ORIENTATIONS.LANDSCAPE : ORIENTATIONS.PORTRAIT;

        setOrientation(layout);
    }, [breakpoint]);

    const debouncedHandleWindowSizeChange = useDebounce(handleWindowSizeChange, debounceMs);

    useEffect(() => {
        handleWindowSizeChange();

        window.addEventListener('resize', debouncedHandleWindowSizeChange);

        return () => {
            window.removeEventListener('resize', debouncedHandleWindowSizeChange);
        };
    }, [handleWindowSizeChange, debouncedHandleWindowSizeChange]);

    return orientation;
};
